// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useEffect, useState } from "react";
import { useKeycloak } from "../context/KeycloakProvider";
import { tenantSettingsService } from "../services/tenantSettingsService";
import { hasSuperAdminAccess } from "../config/roleConfig";
import { TENANT_SETTINGS_UPDATED_EVENT } from "../utils/tenantSettingsEvents";

/**
 * Hook to load tenant-specific localization settings from the backend.
 * Returns language, timezone, date/time formats, and currency.
 */
const useTenantLocalization = () => {
  const { getUserInfo } = useKeycloak();
  const userInfo = getUserInfo();

  const allRoles = [
    ...(userInfo?.roles || []),
    ...(userInfo?.resourceRoles
      ? Object.values(userInfo.resourceRoles).flatMap((r) => r.roles || [])
      : []),
  ];

  const isSuperAdmin = hasSuperAdminAccess(allRoles);
  const tenantId = isSuperAdmin
    ? userInfo?.tenant_id
    : (userInfo?.tenant_id ?? null);

  const [localization, setLocalization] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let isDisposed = false;

    const loadLocalization = async () => {
      if (!tenantId) {
        if (!isDisposed) {
          setLocalization({});
          setError(null);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      try {
        const config = await tenantSettingsService.getTenantConfig(tenantId);
        const nextLocalization = config.localization || {};

        if (!isMounted || isDisposed) return;
        setLocalization(nextLocalization);
        setError(null);
      } catch (err) {
        console.error("Failed to load tenant localization settings:", err);
        if (!isMounted || isDisposed) return;
        setLocalization({});
        setError(err);
      } finally {
        if (isMounted && !isDisposed) {
          setLoading(false);
        }
      }
    };

    loadLocalization();

    const handleTenantSettingsUpdated = (event) => {
      const updatedTenantId = event?.detail?.tenantId ?? null;
      if (!tenantId) return;
      if (updatedTenantId && updatedTenantId !== tenantId) return;
      loadLocalization();
    };
    window.addEventListener(
      TENANT_SETTINGS_UPDATED_EVENT,
      handleTenantSettingsUpdated,
    );

    return () => {
      isMounted = false;
      isDisposed = true;
      window.removeEventListener(
        TENANT_SETTINGS_UPDATED_EVENT,
        handleTenantSettingsUpdated,
      );
    };
  }, [tenantId]);

  const language = localization.default_language || "en";
  const timezone = localization.timezone || "UTC";
  const dateFormat = localization.date_format || "MM/DD/YYYY";
  const timeFormat = localization.time_format || "24h";
  const currency = localization.currency || "EUR";

  return {
    localization,
    loading,
    error,
    language,
    timezone,
    dateFormat,
    timeFormat,
    currency,
  };
};

export default useTenantLocalization;
